import { useState, useEffect } from 'react';
import { Table, Tag, Popover, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowsClockwiseIcon, ChartLineIcon, PencilSimpleIcon, TrashSimpleIcon, UserIcon } from '@phosphor-icons/react';
import TransactionHistory from './TransactionHistory';
import AccountEditModal from './AccountEditModal';
import StatsCard from 'components/ui/StatsCard';
import { WalletIcon, ArrowsCounterClockwiseIcon, CellSignalFullIcon } from '@phosphor-icons/react';
import { API_URL } from 'env';
import { TradeAccount, useData } from 'components/provider/data';

interface Staff {
    name: string;
    id: string;
    email: string;
    avatar: string;
}

interface AccountInfo {
    account: {
        name: string;
        id: string;
        email: string;
        avatar: string;
        link: string;
    };
    platform: {
        name: string;
        version: string;
        link: string;
        id: string;
    };
    balance: number;
    equity: number;
    pl: number;
    staff: Staff;
    status: 'Active' | 'Pending' | 'Inactive';
}

export type { AccountInfo };

const AccountDetailModal = ({ account }: { account: AccountInfo }) => (
    <div className="flex flex-col h-[80vh] overflow-y-auto gap-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatsCard
                title="Balance"
                value={`$${account.balance.toLocaleString()}`}
                icon={<WalletIcon size={20} color="#fff" />}
                iconBg="#2563eb"
            />
            <StatsCard
                title="Equity"
                value={`$${account.equity.toLocaleString()}`}
                icon={<ArrowsCounterClockwiseIcon size={20} color="#fff" />}
                iconBg="#7c3aed"
            />
            <StatsCard
                title="P&L"
                value={`$${account.pl.toLocaleString()}`}
                icon={<CellSignalFullIcon size={20} color="#fff" />}
                iconBg={account.pl >= 0 ? '#16a34a' : '#dc2626'}
            />
        </div>
        <TransactionHistory
            option={{
                accountLoginId: account.platform.id,
                platform: account.platform.name,
                accountName: account.account.name,
                title: false,
                table: true,
                filter: true,
                pagination: true,
                randomize: true,
                randomizeInterval: 1000,
                border: false,
                shadow: false,
                padding: { top: '20px', bottom: '20px', left: '0', right: '0' },
                colWidth: ['w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[20%]', 'w-[10%]'],
            }}
        />
    </div>
);

const ActiveTradingAccountList = () => {
    const [data, setData] = useState<AccountInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [spinning, setSpinning] = useState(false);
    const [page, setPage] = useState(1);
    const [detailAccount, setDetailAccount] = useState<AccountInfo | null>(null);
    const [editAccount, setEditAccount] = useState<AccountInfo | null>(null);
    const { setTradeAccounts } = useData();

    const fetchData = async () => {
        try {
            const response = await fetch(`${API_URL}api/v1/be/trade_accounts`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const json = await response.json();
            const accounts: AccountInfo[] = json.data.items.map((item: any) => ({
                account: {
                    name: item.user_display?.name || item.user?.fullname || item.nickname || 'Unknown',
                    id: item.id,
                    email: item.user_display?.email || item.user?.email || '-',
                    avatar: item.user_display?.avatar || item.user?.avatar || '',
                    link: `/account/${item.id}`,
                },
                platform: {
                    name: item.platform || 'Unknown',
                    version: item.platform_version || '-',
                    link: item.platform_link || '#',
                    id: item.account_number || '-',
                },
                balance: item.balance ?? 0,
                equity: item.equity ?? 0,
                pl: item.pl ?? 0,
                staff: {
                    name: item.user?.fullname || 'No User',
                    id: item.user?.id || '',
                    avatar: item.user?.avatar_url || '',
                    email: item.user?.email || '-',
                },
                status: item.is_active ? 'Active' : 'Inactive',
            }));
            setData(accounts);
            setTradeAccounts(accounts.map((a): TradeAccount => ({
                id: a.account.id,
                login_id: a.platform.id,
                nickname: a.account.name,
                platform: a.platform.name,
                broker: a.platform.version,
                balance: a.balance,
                equity: a.equity,
                pl: a.pl,
                linked_user: { id: a.staff.id, name: a.staff.name, email: a.staff.email },
            })));
        } catch (err) {
            console.error('Error fetching accounts:', err);
        } finally {
            setLoading(false);
            setSpinning(false);
        }
    };

    useEffect(() => {
        fetchData();
        const timer = setInterval(fetchData, 60000);
        return () => clearInterval(timer);
    }, []);

    const handleRefresh = () => {
        setSpinning(true);
        fetchData();
    };

    const columns: ColumnsType<AccountInfo> = [
        {
            title: 'Account',
            key: 'account',
            render: (_, item) => (
                <div>
                    <a href={item.account.link} className="text-blue-500 hover:underline text-sm font-medium">
                        {item.account.name}
                    </a>
                    <p className="text-xs text-text-muted m-0">ID: {item.account.id}</p>
                </div>
            ),
        },
        {
            title: 'Platform',
            key: 'platform',
            render: (_, item) => (
                <div className="flex flex-col gap-1">
                    <Tag className="w-fit">{item.platform.name}</Tag>
                    <span className="text-xs text-text-muted">{item.platform.id}</span>
                </div>
            ),
        },
        {
            title: 'Balance',
            key: 'balance',
            render: (_, item) => <span className="text-sm">${item.balance.toLocaleString()}</span>,
        },
        {
            title: 'Equity',
            key: 'equity',
            render: (_, item) => <span className="text-sm">${item.equity.toLocaleString()}</span>,
        },
        {
            title: 'P&L',
            key: 'pl',
            render: (_, item) => (
                <span className={`text-sm font-medium ${item.pl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {item.pl > 0 ? '+' : item.pl < 0 ? '-' : ''}${Math.abs(item.pl).toLocaleString()}
                </span>
            ),
        },
        {
            title: 'User',
            key: 'user',
            render: (_, item) => (
                <Popover
                    arrow={false}
                    title="User Info"
                    content={
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center shrink-0">
                                <UserIcon size={16} color="#fff" />
                            </div>
                            <div>
                                <p className="text-sm m-0 font-medium">{item.staff.name}</p>
                                <p className="text-xs text-text-muted m-0">{item.staff.email}</p>
                            </div>
                        </div>
                    }
                >
                    <a href={`/users/${item.staff.id}`} className="text-blue-500 hover:underline text-sm">
                        {item.staff.name}
                    </a>
                </Popover>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, item) => (
                <Tag color={item.status === 'Active' ? 'green' : item.status === 'Pending' ? 'gold' : 'default'}>
                    {item.status}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 110,
            render: (_, item) => (
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setDetailAccount(item)}
                        className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        title="View details"
                    >
                        <ChartLineIcon size={16} />
                    </button>
                    <button
                        onClick={() => setEditAccount(item)}
                        className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors cursor-pointer text-blue-500"
                        title="Edit"
                    >
                        <PencilSimpleIcon size={16} />
                    </button>
                    <button
                        className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900 transition-colors cursor-pointer text-red-500"
                        title="Delete"
                    >
                        <TrashSimpleIcon size={16} />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="bg-bg-card border border-border rounded-xl shadow-card">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h2 className="text-base font-semibold m-0">Active Trading Accounts</h2>
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-text-muted">
                            Last updated: {new Date().toLocaleTimeString()}
                        </span>
                        <button
                            onClick={handleRefresh}
                            className="p-1.5 rounded hover:bg-bg-hover transition-colors cursor-pointer"
                            title="Refresh"
                        >
                            <ArrowsClockwiseIcon
                                size={18}
                                className={`text-text-secondary ${spinning ? 'animate-spin' : ''}`}
                            />
                        </button>
                    </div>
                </div>

                <Table
                    columns={columns}
                    dataSource={data.slice((page - 1) * 10, page * 10)}
                    rowKey={r => r.account.id}
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: page,
                        pageSize: 10,
                        total: data.length,
                        onChange: setPage,
                        showTotal: total => `Tổng ${total} tài khoản`,
                        showSizeChanger: false,
                    }}
                />
            </div>

            <Modal
                title="Account Details"
                open={!!detailAccount}
                onCancel={() => setDetailAccount(null)}
                footer={null}
                width={1000}
                centered
            >
                {detailAccount && <AccountDetailModal account={detailAccount} />}
            </Modal>

            <Modal
                title="Edit Account"
                open={!!editAccount}
                onCancel={() => setEditAccount(null)}
                footer={null}
                width={800}
                centered
            >
                {editAccount && (
                    <AccountEditModal
                        account={editAccount.account.id}
                        onClose={() => setEditAccount(null)}
                    />
                )}
            </Modal>
        </>
    );
};

export default ActiveTradingAccountList;
